/**
 * User Categories API — public fetch (for the "I Am A" selects) + super-admin CRUD.
 * Mirrors the geo/locations services (api.ts fetchGeoCountries + adminGeoApi.ts).
 */

import { UserCategory } from '../data/userCategories';
import { withLocale } from '../i18n/standalone';

const API_URL = process.env.REACT_APP_API_URL || '';

/** Public: active categories, ordered. Returns null on any failure (caller falls back). */
export async function fetchUserCategories(): Promise<UserCategory[] | null> {
  try {
    const res = await fetch(withLocale(`${API_URL}/api/user-categories.php`), { signal: AbortSignal.timeout(4000) });
    if (!res.ok) return null;
    const data = await res.json();
    const list = data?.categories;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((c: any) => ({
      id: c.id ?? null,
      name: String(c.name),
      // Older servers send only `name`, which was English there anyway.
      nameEn: String(c.nameEn ?? c.name),
      slug: String(c.slug),
    }));
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Admin CRUD (super-admin; cookie-credentialed)
// ---------------------------------------------------------------------------

export interface AdminUserCategory {
  id: number;
  name: string;
  slug: string;
  sortOrder: number | null;
  isActive: boolean;
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
}

export async function fetchAdminUserCategories(): Promise<AdminUserCategory[]> {
  const res = await fetch(`${API_URL}/api/admin-user-categories.php`, { credentials: 'include' });
  const data = await asJson(res);
  return (data.categories ?? []) as AdminUserCategory[];
}

export async function createUserCategory(payload: { name: string; slug?: string; sortOrder?: number | null }): Promise<AdminUserCategory> {
  const res = await fetch(`${API_URL}/api/admin-user-categories.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return (await asJson(res)).category as AdminUserCategory;
}

export async function updateUserCategory(
  id: number,
  payload: Partial<{ name: string; slug: string; isActive: boolean; sortOrder: number | null }>,
): Promise<AdminUserCategory> {
  const res = await fetch(`${API_URL}/api/admin-user-categories.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return (await asJson(res)).category as AdminUserCategory;
}

export async function deleteUserCategory(id: number): Promise<void> {
  const res = await fetch(`${API_URL}/api/admin-user-categories.php?id=${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await asJson(res);
}
